import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../config/apiBaseUrl';
import {
  setActiveAccountContext,
  subscribeToAccountContextInvalidation,
} from '../services/api';

const AuthContext = createContext(null);

// Configure axios defaults
axios.defaults.baseURL = API_BASE_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState(null);
  const [accountContextVersion, setAccountContextVersion] = useState(0);

  const initializeAccountContext = (currentUser, availableAccounts) => {
    const personal = availableAccounts.find((account) => account.isPersonal)
      || { id: currentUser.id, name: 'My Account', role: 'OWNER', isPersonal: true };
    const storageKey = `receipt-tracker:active-account:${currentUser.id}`;
    const storedId = localStorage.getItem(storageKey);
    const stored = availableAccounts.find((account) => account.id === storedId);
    const selected = stored || personal;
    if (!stored && storedId) localStorage.removeItem(storageKey);
    setActiveAccountContext(selected.isPersonal ? null : selected.id);
    setActiveAccount(selected);
    setAccounts(availableAccounts.length ? availableAccounts : [personal]);
    return selected;
  };

  const fetchCurrentSession = async () => {
    const response = await axios.get('/api/auth/me');
    const currentUser = response.data.user;
    initializeAccountContext(currentUser, response.data.accounts || []);
    setUser(currentUser);
    return currentUser;
  };

  // Set auth token in axios headers
  const setAuthToken = (token) => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  };

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    
    if (token) {
      setAuthToken(token);
      // Verify token with backend
      fetchCurrentSession()
        .catch(error => {
          console.error('Token verification failed:', error);
          setAuthToken(null);
          setUser(null);
          localStorage.removeItem('user');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
    // Intentionally run once on mount only — fetchCurrentSession is redefined
    // every render, so adding it here would re-verify the session on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => subscribeToAccountContextInvalidation(() => {
    if (!user) return;
    const personal = accounts.find((account) => account.isPersonal)
      || { id: user.id, name: 'My Account', role: 'OWNER', isPersonal: true };
    localStorage.removeItem(`receipt-tracker:active-account:${user.id}`);
    setActiveAccount(personal);
    setAccountContextVersion((version) => version + 1);
    toast.error('Shared account access ended. Switched back to your account.');
    if (window.location.pathname !== '/dashboard') {
      window.location.assign('/dashboard');
    }
  }), [accounts, user]);

  const refreshAccounts = async () => {
    if (!localStorage.getItem('token')) return [];
    const response = await axios.get('/api/auth/me');
    const nextAccounts = response.data.accounts || [];
    setUser(response.data.user);
    setAccounts(nextAccounts);
    if (activeAccount && !nextAccounts.some((account) => account.id === activeAccount.id)) {
      const personal = nextAccounts.find((account) => account.isPersonal);
      setActiveAccountContext(null);
      setActiveAccount(personal || null);
      localStorage.removeItem(`receipt-tracker:active-account:${response.data.user.id}`);
      setAccountContextVersion((version) => version + 1);
    }
    return nextAccounts;
  };

  const switchAccount = (accountId, availableAccounts = accounts) => {
    if (!user) return false;
    const selected = availableAccounts.find((account) => account.id === accountId);
    if (!selected) return false;
    const storageKey = `receipt-tracker:active-account:${user.id}`;
    if (selected.isPersonal) {
      localStorage.removeItem(storageKey);
      setActiveAccountContext(null);
    } else {
      localStorage.setItem(storageKey, selected.id);
      setActiveAccountContext(selected.id);
    }
    setActiveAccount(selected);
    setAccountContextVersion((version) => version + 1);
    return true;
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      
      const { user, token } = response.data;
      
      setAuthToken(token);
      await fetchCurrentSession();
      localStorage.setItem('user', JSON.stringify(user));
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const signup = async (email, password, firstName, lastName) => {
    try {
      const response = await axios.post('/api/auth/signup', {
        email,
        password,
        firstName,
        lastName,
      });

      const { user, token, requiresEmailVerification, message } = response.data;

      // Signup may intentionally not return a token when email verification is required.
      if (token && user) {
        setAuthToken(token);
        setUser(user);
        localStorage.setItem('user', JSON.stringify(user));
      }

      return {
        success: true,
        requiresEmailVerification: Boolean(requiresEmailVerification || !token),
        message:
          message || 'Account created. Please confirm your email, then sign in.',
      };
    } catch (error) {
      console.error('Signup error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Signup failed' 
      };
    }
  };

  const loginWithGoogle = async (credentialResponse) => {
    try {
      // The credential is a JWT token from Google
      // We'll send it to our backend for verification
      const response = await axios.post('/api/auth/google/verify', {
        credential: credentialResponse.credential,
      });
      
      const { user, token } = response.data;
      
      setAuthToken(token);
      await fetchCurrentSession();
      localStorage.setItem('user', JSON.stringify(user));
      
      return { success: true };
    } catch (error) {
      console.error('Google login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Google login failed' 
      };
    }
  };

  const logout = () => {
    setActiveAccountContext(null);
    setAuthToken(null);
    setUser(null);
    setAccounts([]);
    setActiveAccount(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  const forgotPassword = async (email) => {
    try {
      const response = await axios.post('/api/auth/forgot-password', { email });
      return {
        success: true,
        message:
          response.data?.message ||
          'If an account exists for that email, a reset link has been sent.',
      };
    } catch (error) {
      console.error('Forgot password error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to process forgot password request',
      };
    }
  };

  const resetPassword = async (token, password) => {
    try {
      const response = await axios.post('/api/auth/reset-password', { token, password });
      return {
        success: true,
        message: response.data?.message || 'Password reset successful',
      };
    } catch (error) {
      console.error('Reset password error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reset password',
      };
    }
  };

  const verifyEmail = async (token) => {
    try {
      const response = await axios.post('/api/auth/verify-email', { token });
      return {
        success: true,
        message: response.data?.message || 'Email verified successfully',
      };
    } catch (error) {
      console.error('Verify email error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to verify email',
      };
    }
  };

  const value = {
    user,
    loading,
    login,
    signup,
    loginWithGoogle,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    accounts,
    sharedAccounts: accounts.filter((account) => !account.isPersonal),
    activeAccount,
    switchAccount,
    refreshAccounts,
    accountContextVersion,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
